import React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Dialog } from '@betareader/design-system';
import styled from 'styled-components';
import useAudiobookGeneration, {
  AudiobookStatusChapter,
} from 'src/hooks/useAudiobookGeneration';
import FeedbackButton from 'src/components/feedback/FeedbackButton';
import Icon from '../AppIcon';
import AudiobookParagraphStudio from './audiobookParagraphStudio';

// Stable, UNTRANSLATED tag stored on every audiobook feedback row so the
// `Feedback` collection can be filtered by feature without splitting per UI
// language (the tooltip/label may localize, the stored tag must not).
const AUDIOBOOK_FEEDBACK_FEATURE = 'Audiobook';

/**
 * The access-ON state of the author Audiobook panel: an explicit view to
 * GENERATE the audiobook (with the cost visible) and PREVIEW/listen to the
 * result — so it's obvious how to produce audio and what it costs, instead of
 * dropping the author into the reader. Reuses the existing generation +
 * streaming endpoints (see hooks/useAudiobookGeneration.ts). Real credits are
 * charged on generation (20 credits / 1,000 words per chapter).
 */

const CREDIT_USD = 0.1; // 1 credit = $0.10 (creditPricing.ts peg)

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
`;

const ReaderAccessCard = styled.section<{ $published: boolean }>`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 14px;
  margin: 22px 0 4px;
  padding: 15px 17px;
  border: 1px solid ${({ $published }) => ($published ? '#c9b8ef' : '#ded9d0')};
  border-left: 4px solid
    ${({ $published }) => ($published ? '#6435c9' : '#9a958c')};
  border-radius: 8px;
  background: ${({ $published }) => ($published ? '#f8f5ff' : '#faf9f7')};

  @media (max-width: 580px) {
    grid-template-columns: auto minmax(0, 1fr);
    padding: 14px;

    > label {
      grid-column: 1 / -1;
      justify-self: stretch;
    }
  }
`;

const AccessState = styled.span<{ $published: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $published }) => ($published ? '#6435c9' : '#9a958c')};
  box-shadow: 0 0 0 4px
    ${({ $published }) => ($published ? '#e9e0fb' : '#ece9e3')};
`;

const AccessToggle = styled.label`
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 9px;
  min-height: 38px;
  color: var(--br-text);
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;

  input {
    appearance: none;
    position: relative;
    width: 38px;
    height: 22px;
    margin: 0;
    border: 1px solid #aaa49a;
    border-radius: 999px;
    background: #dedad3;
    cursor: pointer;
    transition:
      background 140ms ease,
      border-color 140ms ease;
  }

  input::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: var(--br-surface);
    box-shadow: 0 1px 3px rgba(51, 45, 62, 0.28);
    transition: transform 140ms ease;
  }

  input:checked {
    border-color: #6435c9;
    background: #6435c9;
  }

  input:checked::after {
    transform: translateX(16px);
  }

  input:focus-visible {
    outline: 3px solid rgba(100, 53, 201, 0.3);
    outline-offset: 3px;
  }

  input:disabled {
    cursor: wait;
  }

  @media (prefers-reduced-motion: reduce) {
    input,
    input::after {
      transition: none;
    }
  }
`;

const usd = (credits: number) =>
  `$${(Math.round(credits * CREDIT_USD * 100) / 100).toLocaleString()}`;

type Props = {
  bookId?: string;
  contentVersionId?: string;
  bookTitle?: string;
  narratorName?: string;
  embedded?: boolean;
  canManageReaderAccess?: boolean;
  generationEnabled?: boolean;
  unavailableFallback?: React.ReactNode;
};

const StageTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 9px;
  margin: 0;
`;

const StageNumber = styled.span`
  display: inline-grid;
  width: 24px;
  height: 24px;
  place-items: center;
  border-radius: 50%;
  background: #6435c9;
  color: #fff;
  font-size: 12px;
  font-weight: 800;
`;

const QueueTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 10px 8px;
    text-align: left;
    border-bottom: 1px solid var(--br-border);
  }

  th:nth-child(2), th:nth-child(3), th:nth-child(5),
  td:nth-child(2), td:nth-child(3), td:nth-child(5) {
    text-align: right;
  }

  @media (max-width: 580px) {
    &&& thead {
      display: none;
    }

    &&& tbody,
    &&& tr {
      display: block;
      width: 100%;
    }

    &&& tr {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      gap: 8px 12px;
      padding: 15px 0;
      border-bottom: 1px solid #e4e0da;
    }

    &&& td {
      display: block;
      width: auto !important;
      padding: 0 !important;
      border: 0 !important;
    }

    &&& td:nth-child(1) {
      grid-column: 1 / -1;
      font-weight: 700;
    }

    &&& td:nth-child(2),
    &&& td:nth-child(3) {
      color: var(--br-muted);
      text-align: left;
    }

    &&& td:nth-child(3) {
      text-align: right;
    }

    &&& td:nth-child(5) {
      text-align: right;
    }
  }
`;

const StatusBadge = styled.span<{ $tone?: 'violet' | 'green' | 'blue' | 'grey' | 'red' }>`
  display: inline-block;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  color: ${({ $tone }) => $tone === 'red' ? '#a32121' : $tone === 'green' ? '#216c37' : $tone === 'blue' ? '#24599a' : $tone === 'violet' ? '#6435c9' : '#555'};
  background: ${({ $tone }) => $tone === 'red' ? '#fce9e9' : $tone === 'green' ? '#e8f4eb' : $tone === 'blue' ? '#eaf2fd' : $tone === 'violet' ? '#f1ebfa' : '#eee'};
`;

const statusLabel = (
  t: (k: string, d?: string) => string,
  chapter?: AudiobookStatusChapter
) => {
  const status = chapter?.status;
  const timedOut =
    chapter?.error === 'PROVIDER_TIMEOUT' ||
    chapter?.error === 'STALE_TIMEOUT' ||
    /\btimeout(?: of \d+ms)? exceeded\b/i.test(chapter?.error || '') ||
    /\btimed out\b/i.test(chapter?.error || '');
  switch (status) {
    case 'draft':
      return <StatusBadge $tone="violet">In studio</StatusBadge>;
    case 'ready':
      return (
        <StatusBadge $tone="green">
          {t('AudiobookStatusReady', 'Ready')}
        </StatusBadge>
      );
    case 'generating':
      return (
        <StatusBadge $tone="blue">
          {t('AudiobookStatusGenerating', 'Generating…')}
        </StatusBadge>
      );
    case 'queued':
      return (
        <StatusBadge $tone="grey">
          {t('AudiobookStatusQueued', 'Queued')}
        </StatusBadge>
      );
    case 'error':
      return (
        <StatusBadge $tone="red">
          {timedOut
            ? t('AudiobookStatusTimedOut', 'Timed out')
            : t('AudiobookStatusError', 'Error')}
        </StatusBadge>
      );
    default:
      return (
        <StatusBadge>
          {t('AudiobookStatusNone', 'Not generated')}
        </StatusBadge>
      );
  }
};

const AudiobookGenerationPanel: React.FC<Props> = ({
  bookId,
  contentVersionId,
  bookTitle,
  narratorName,
  embedded = false,
  canManageReaderAccess = true,
  generationEnabled = true,
  unavailableFallback = null,
}) => {
  const { t } = useTranslation();
  const {
    preview,
    previewLoading,
    previewError,
    statusLoading,
    statusByPart,
    convert,
    getStreamUrl,
    readerAccess,
    setReaderAccess,
    refetchStatus,
  } = useAudiobookGeneration(bookId, contentVersionId, {
    generationEnabled,
  });

  const [previewPartId, setPreviewPartId] = React.useState<string | null>(null);
  const [studioPartId, setStudioPartId] = React.useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [previewErr, setPreviewErr] = React.useState<string | null>(null);
  const [generatingPartId, setGeneratingPartId] = React.useState<string | null>(
    null
  );
  // Pending credit spend awaiting confirmation. Every chapter, regeneration,
  // and whole-book request charges real credits. null = nothing pending.
  const [pendingGen, setPendingGen] = React.useState<{
    partIds: string[];
    singlePartId?: string;
    credits: number;
    title: string;
  } | null>(null);
  // Whether the "generation not set up yet" dialog is showing. Opened when a
  // convert fails with GENERATION_UNAVAILABLE (the server has no TTS provider),
  // dismissable — this is NOT a user error, so it must not read as a red banner.
  const [unavailableOpen, setUnavailableOpen] = React.useState(false);

  const chapters = preview?.chapters || [];
  const billable = chapters.filter(c => c.hasText && !statusByPart[c.partId]?.inStudio);
  const totalCredits = billable.reduce((sum, chapter) => sum + chapter.credits, 0);
  const balance = preview?.credits?.balance;
  const insufficient = typeof balance === 'number' && balance < totalCredits;

  const playPreview = async (partId: string) => {
    setPreviewErr(null);
    setPreviewPartId(partId);
    setPreviewUrl(null);
    try {
      const { url } = await getStreamUrl(partId);
      setPreviewUrl(url);
    } catch (e: any) {
      setPreviewErr(
        e?.message || t('AudiobookPreviewFailed', 'Could not load audio.')
      );
    }
  };

  const generate = async (partIds: string[], singlePartId?: string) => {
    setGeneratingPartId(singlePartId || '__all__');
    try {
      await convert.mutateAsync(partIds);
    } catch (e: any) {
      // GENERATION_UNAVAILABLE (server has no TTS provider) isn't a user error —
      // open the gentle "being set up" dialog. Opening HERE, on each attempt,
      // means a re-click after dismissing re-shows it (an effect keyed on the
      // error would not, since the code stays the same). Other errors surface in
      // the banner via `convert.error`. Swallow the rejection either way so the
      // onClick promise doesn't become an unhandled rejection.
      if (e?.code === 'GENERATION_UNAVAILABLE') setUnavailableOpen(true);
    } finally {
      setGeneratingPartId(null);
    }
  };

  const renderReaderAccessControl = () => {
    if (
      !canManageReaderAccess ||
      (!readerAccess.published && readerAccess.readyChapterCount === 0)
    ) {
      return null;
    }

    return (
      <ReaderAccessCard
        $published={readerAccess.published}
        data-testid="audiobook-reader-access"
      >
        <>
          <AccessState $published={readerAccess.published} aria-hidden="true" />
          <div>
            <>
              <div style={{ fontWeight: 800, color: 'var(--br-text)' }}>
                {readerAccess.published
                  ? t('AudiobookReaderAccessOn', 'Available to readers')
                  : t('AudiobookReaderAccessOff', 'Private')}
              </div>
              <div style={{ color: 'var(--br-muted)', fontSize: 12, marginTop: 2 }}>
                {readerAccess.published
                  ? t(
                      'AudiobookReaderAccessOnHelp',
                      '{{n}} ready chapters can be listened to in the reader app.',
                      { n: readerAccess.readyChapterCount }
                    )
                  : t(
                      'AudiobookReaderAccessOffHelp',
                      'Only you can preview the {{n}} ready chapters until you make them available.',
                      { n: readerAccess.readyChapterCount }
                    )}
              </div>
              {readerAccess.published && readerAccess.hasUnpublishedAudio && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ color: 'var(--br-muted)', fontSize: 12, marginBottom: 6 }}>
                    {t('AudiobookNewAudioPrivate',
                      'New audio is ready. Readers still hear the previously published version.')}
                  </div>
                  <Button
                    size="small"
                    type="button"
                    disabled={setReaderAccess.isPending}
                    onClick={() => {
                      Promise.resolve(setReaderAccess.mutateAsync(true)).catch(
                        () => undefined
                      );
                    }}
                  >
                    {t('AudiobookPublishLatestAudio', 'Publish latest audio')}
                  </Button>
                </div>
              )}
              {Boolean(setReaderAccess.error) && (
                <div
                  role="alert"
                  style={{ color: '#b03060', fontSize: 12, marginTop: 5 }}
                >
                  {(setReaderAccess.error as any).message}
                </div>
              )}
            </>
          </div>
          <AccessToggle>
            <input
              type="checkbox"
              checked={readerAccess.published}
              disabled={setReaderAccess.isPending}
              onChange={event => {
                // Promise.resolve also tolerates the lightweight mutation mock
                // used by the focused component test.
                Promise.resolve(
                  setReaderAccess.mutateAsync(event.target.checked)
                ).catch(() => undefined);
              }}
              data-testid="audiobook-reader-access-toggle"
              aria-label={t(
                'AudiobookReaderAccessToggle',
                'Make ready audiobook chapters available to readers'
              )}
            />
            {setReaderAccess.isPending
              ? t('Saving', 'Saving…')
              : t('AudiobookReaderAccessToggleLabel', 'Reader access')}
          </AccessToggle>
        </>
      </ReaderAccessCard>
    );
  };

  if (studioPartId && bookId && contentVersionId && generationEnabled) {
    return <AudiobookParagraphStudio
      bookId={bookId}
      contentVersionId={contentVersionId}
      partId={studioPartId}
      onClose={() => { setStudioPartId(null); refetchStatus(); }}
    />;
  }

  if (!generationEnabled) {
    if (statusLoading) {
      return (
        <Card role="status" style={{ padding: 40, textAlign: 'center' }}>
          {t('AudiobookLoadingExisting', 'Loading existing audiobook…')}
        </Card>
      );
    }

    if (!readerAccess.published && readerAccess.readyChapterCount === 0) {
      return <>{unavailableFallback}</>;
    }

    return (
      <div data-testid="audiobook-existing-management">
        <StageTitle>
          <Icon name="headphones" />
          <span>{t('AudiobookExisting', 'Existing audiobook')}</span>
          <StatusBadge $tone="violet">
            {t('BetaLabel', 'Beta')}
          </StatusBadge>
        </StageTitle>
        <p style={{ color: 'var(--br-muted)', marginTop: 8, maxWidth: 560 }}>
          {t(
            'AudiobookGenerationOffExistingHelp',
            'New generation is currently paused. Your existing audio remains available to preview and manage.'
          )}
        </p>
        {renderReaderAccessControl()}
      </div>
    );
  }

  if (previewLoading) {
    return (
      <Card role="status" style={{ padding: 40, textAlign: 'center' }}>
        {t('AudiobookLoadingCost', 'Estimating cost…')}
      </Card>
    );
  }

  // A 402 here means the feature is on but the account can't afford the whole
  // book; we still render the table so per-chapter generation is possible.
  const convertErr: any = convert.error;
  // Keep GENERATION_UNAVAILABLE out of the red banner — it's handled by the
  // gentle dialog opened in `generate`'s catch (on the click), never a banner.
  const generationUnavailable = convertErr?.code === 'GENERATION_UNAVAILABLE';

  return (
    <div data-testid="audiobook-generation-panel">
      <Header>
        <div>
          <StageTitle>
            {embedded && <StageNumber>2</StageNumber>}
            <span>
              {!embedded && <Icon name="headphones" />}{' '}
              {embedded
                ? t('AudiobookProduction', 'Generate your audiobook')
                : t('Audiobook', 'Audiobook')}
            </span>
            <StatusBadge
              $tone="violet"
              style={{ verticalAlign: 'middle' }}
            >
              {t('BetaLabel', 'Beta')}
            </StatusBadge>
            {/* Feature feedback (mirrors critiqueGroupList). The STORED tag is a
                constant — never t(...) — so reports don't split by language. */}
            <FeedbackButton
              featureName={AUDIOBOOK_FEEDBACK_FEATURE}
              url={window.location.pathname}
            />
          </StageTitle>
          <p style={{ color: 'var(--br-muted)', marginTop: 6, maxWidth: 560 }}>
            {embedded
              ? `Narrated by ${narratorName || 'your selected voice'}. Generate the book in one pass, or open a chapter studio to direct individual paragraphs. Each path shows its own cost before charging.`
              : t(
                  'AudiobookGenerateIntro',
                  'Generate “{{title}}” in one pass, or open a chapter studio to direct individual paragraphs. Each path shows its own cost before charging.',
                  { title: bookTitle || t('YourBook', 'your book') }
                )}
          </p>
        </div>
        <Card style={{ margin: 0 }}>
          <div style={{ fontSize: 13, color: 'var(--br-muted)' }}>
            {t('AudiobookOnePassWholeBook', 'Whole book · one pass')}
          </div>
          <div style={{ fontSize: 22, fontWeight: 800 }}>
            {usd(totalCredits)}{' '}
            <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--br-muted)' }}>
              ·{' '}
              {t('AudiobookCredits', '{{n}} credits', {
                n: totalCredits.toLocaleString(),
              })}
            </span>
          </div>
          {typeof balance === 'number' && (
            <div
              style={{
                fontSize: 12,
                color: insufficient ? '#c0392b' : '#6b675e',
                marginTop: 4,
              }}
            >
              {t('AudiobookBalance', 'Balance: {{n}} credits', {
                n: balance.toLocaleString(),
              })}
            </div>
          )}
        </Card>
      </Header>

      {previewError && (
        <Alert variant="error">
          {previewError.code === 'FEATURE_DISABLED'
            ? t(
                'AudiobookFeatureDisabled',
                'Audiobook generation is not enabled for your account.'
              )
            : previewError.message}
        </Alert>
      )}

      {insufficient && (
        <Alert
          variant="warning"
          data-testid="audiobook-insufficient-credits"
        >
          <Icon name="exclamation triangle" />
          {t(
            'AudiobookInsufficientCredits',
            'Not enough credits for the whole book. You can still generate individual chapters, or top up your credits.'
          )}
        </Alert>
      )}

      {/* GENERATION_UNAVAILABLE is NOT a user error — it opens the dialog below,
          so keep it out of the red banner. */}
      {convertErr && !generationUnavailable && (
        <Alert variant="error">
          {convertErr.code === 'LANGUAGE_NOT_SUPPORTED'
            ? t(
                'AudiobookLanguageUnsupported',
                'Audiobook generation currently supports English and Swedish only.'
              )
            : convertErr.code === 'INSUFFICIENT_CREDITS'
              ? t(
                  'AudiobookInsufficientForSelection',
                  'Not enough credits for that selection.'
                )
              : convertErr.message}
        </Alert>
      )}

      {renderReaderAccessControl()}

      <h3 style={{ margin: '22px 0 4px' }}>
        {t('AudiobookProductionQueue', 'Production queue')}
      </h3>
      <div style={{ color: 'var(--br-muted)', fontSize: 12, marginBottom: 8 }}>
        One-pass chapter prices are shown here. Open a studio for paragraph prices and direction controls. Package the book from the studio when every chapter is ready.
      </div>
      <QueueTable>
        <thead>
          <tr>
            <th scope="col">{t('Chapter', 'Chapter')}</th>
            <th scope="col">
              {t('Words', 'Words')}
            </th>
            <th scope="col">
              {t('AudiobookOnePassCost', 'One-pass cost')}
            </th>
            <th scope="col">{t('Status', 'Status')}</th>
            <th scope="col"><span className="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          {chapters.map(c => {
            const st = statusByPart[c.partId];
            const isReady = st?.status === 'ready';
            const inStudio = Boolean(st?.inStudio);
            const inProgress =
              st?.status === 'queued' || st?.status === 'generating';
            const rowBusy =
              (convert.isPending && generatingPartId === c.partId) ||
              inProgress;
            return (
              <tr
                key={c.partId}
                data-testid={`audiobook-chapter-${c.partId}`}
              >
                <td>{c.title}</td>
                <td>
                  {c.wordCount.toLocaleString()}
                </td>
                <td>
                  {c.hasText ? (
                    <>
                      {usd(c.credits)}{' '}
                      <span style={{ color: 'var(--br-muted)', fontSize: 12 }}>
                        ({c.credits})
                      </span>
                    </>
                  ) : (
                    <span style={{ color: 'var(--br-muted)' }}>—</span>
                  )}
                </td>
                <td>{statusLabel(t, st)}</td>
                <td>
                  {c.hasText && <Button
                    size="small"
                    variant="secondary"
                    disabled={rowBusy}
                    onClick={() => setStudioPartId(c.partId)}
                    data-testid={`audiobook-studio-${c.partId}`}
                  >
                    <Icon name="edit" /> Open studio
                  </Button>}
                  {inStudio ? (isReady ? <Button size="small" variant="secondary" onClick={() => playPreview(c.partId)}>
                    <Icon name="play" /> Preview chapter
                  </Button> : null) : isReady ? (
                    <>
                      <Button
                        size="small"
                        onClick={() => playPreview(c.partId)}
                        data-testid={`audiobook-preview-${c.partId}`}
                      >
                        <Icon name="play" />
                        {t('AudiobookPreview', 'Preview')}
                      </Button>
                      <Button
                        size="small"
                        variant="secondary"
                        aria-busy={rowBusy}
                        disabled={rowBusy}
                        onClick={() =>
                          setPendingGen({
                            partIds: [c.partId],
                            singlePartId: c.partId,
                            credits: c.credits,
                            title: c.title,
                          })
                        }
                      >
                        {t('AudiobookRegenerate', 'Regenerate')} ·{' '}
                        {usd(c.credits)}
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="small"
                      disabled={!c.hasText || rowBusy}
                      aria-busy={rowBusy}
                      onClick={() =>
                        setPendingGen({
                          partIds: [c.partId],
                          singlePartId: c.partId,
                          credits: c.credits,
                          title: c.title,
                        })
                      }
                      data-testid={`audiobook-generate-${c.partId}`}
                    >
                      {inProgress
                        ? t('AudiobookStatusGenerating', 'Generating…')
                        : `${t('AudiobookGenerateChapter', 'Generate')} · ${usd(
                            c.credits
                          )}`}
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </QueueTable>

      {previewPartId && (
        <Card
          data-testid="audiobook-preview-player"
          style={{ marginTop: 8 }}
        >
          <div style={{ fontSize: 13, color: 'var(--br-muted)', marginBottom: 8 }}>
            {t('AudiobookNowPreviewing', 'Previewing:')}{' '}
            {chapters.find(c => c.partId === previewPartId)?.title}
          </div>
          {previewErr ? (
            <Alert variant="error">
              {previewErr}
            </Alert>
          ) : previewUrl ? (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <audio
              controls
              autoPlay
              src={previewUrl}
              style={{ width: '100%' }}
            />
          ) : (
            <span role="status">Loading preview…</span>
          )}
        </Card>
      )}

      <div style={{ marginTop: 16 }}>
        <Button
          disabled={!billable.length || convert.isPending}
          aria-busy={convert.isPending && generatingPartId === '__all__'}
          onClick={() =>
            setPendingGen({
              partIds: billable.map(c => c.partId),
              credits: totalCredits,
              title: bookTitle || t('YourBook', 'your book'),
            })
          }
          data-testid="audiobook-generate-all"
        >
          <Icon name="lightning" />
          {t('AudiobookGenerateAll', 'Generate audiobook')} ·{' '}
          {usd(totalCredits)}
        </Button>
        <span style={{ color: 'var(--br-muted)', fontSize: 12, marginLeft: 10 }}>
          {t(
            'AudiobookServeFree',
            'Generating costs credits; listening is free.'
          )}
        </span>
      </div>

      {/* Confirm real credit spends (whole-book + regenerate) before charging. */}
      <Dialog
        open={pendingGen != null}
        title={t('AudiobookConfirmTitle', 'Generate audiobook?')}
        onClose={() => setPendingGen(null)}
        actions={
          <>
            <Button variant="secondary" onClick={() => setPendingGen(null)}>{t('Cancel', 'Cancel')}</Button>
            <Button data-testid="audiobook-confirm-go" onClick={() => {
              const p = pendingGen;
              setPendingGen(null);
              if (p) generate(p.partIds, p.singlePartId);
            }}>{t('AudiobookConfirmGo', 'Confirm and generate')}</Button>
          </>
        }
      >
        <p data-testid="audiobook-confirm-generate">{t(
          'AudiobookConfirmBody',
          'This will charge {{credits}} credits ({{usd}}) to your BetaReader balance and narrate “{{title}}”. Listening is free once generated.',
          {
            credits: (pendingGen?.credits ?? 0).toLocaleString(),
            usd: usd(pendingGen?.credits ?? 0),
            title: pendingGen?.title ?? '',
          }
        )}</p>
      </Dialog>

      {/* Generation not switched on server-side yet. The user IS in the beta
          (they have access), so this is an informative "we're setting it up"
          message — NOT a "request access" prompt and NOT a hard error. */}
      <Dialog
        open={unavailableOpen}
        title={t('AudiobookUnavailableTitle', 'Audiobook generation is being set up')}
        onClose={() => setUnavailableOpen(false)}
        actions={
          <>
          <FeedbackButton
            featureName={AUDIOBOOK_FEEDBACK_FEATURE}
            url={window.location.pathname}
            trigger={
              <Button variant="secondary" data-testid="audiobook-unavailable-feedback">
                {t('AudiobookShareFeedback', 'Share feedback')}
              </Button>
            }
          />
          <Button
            onClick={() => setUnavailableOpen(false)}
            data-testid="audiobook-unavailable-ok"
          >
            {t('OK', 'OK')}
          </Button>
          </>
        }
      >
        <p data-testid="audiobook-unavailable-dialog">
          {t(
            'AudiobookUnavailableBody',
            'You’re in the audiobook beta, but generation isn’t switched on on our side just yet. We’re finishing the setup and will let you know the moment it’s ready — nothing you need to do.'
          )}
        </p>
      </Dialog>
    </div>
  );
};

export default AudiobookGenerationPanel;
